/**
 * "Verify your email" — one component, two placements.
 *
 * A BANNER at the top of the dashboard, and an INLINE notice beside a disabled enrol
 * button. They are the same states and the same words in different frames, so they live
 * in one file: two copies would drift, and the moment they disagree the learner cannot
 * tell which one is true.
 *
 * IT IS NOT THE ENFORCEMENT. The server refuses self-enrolment for unverified accounts
 * (`openedx_thinlms_accounts.gating`) and returns `account_not_activated`. This only
 * explains why, and offers the one action that fixes it. A disabled button on its own
 * would be trivially bypassed and, worse, unexplained.
 *
 * IT RENDERS NOTHING UNLESS IT IS SURE. `isAccountVerified` returns `null` when it cannot
 * tell — anonymous, or the request failed — and `null` means stay silent. Telling a
 * verified learner they have not verified is worse than saying nothing at all, because
 * they cannot act on it and will not believe the app afterwards.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../shared/ui';
import {
  isAccountVerified,
  resendActivation,
  type ResendResult,
} from '../shared/lib/activation';
import './routes/account.css';

type Props = {
  username: string;
  /** `banner` spans the page; `inline` sits next to a blocked action. */
  variant?: 'banner' | 'inline';
  /** Overrides the default sentence when the reason is specific (e.g. enrolling). */
  message?: string;
};

const RESEND_COPY: Record<ResendResult, string> = {
  sent: 'Sent. Check your inbox — it can take a minute.',
  already_active: 'Your email is already verified. Refresh to continue.',
  // A rate limit is not a fault, and must not read like one.
  throttled: "You've asked a few times already. Please try again in an hour.",
  failed: "We couldn't send it just now. Please try again shortly.",
};

/**
 * Verification state, shared by the notice and by whatever it is blocking.
 *
 * One query key, so a page that both shows the notice AND disables a button asks once and
 * the two can never disagree — a disabled Enrol button beside no explanation, or an
 * explanation beside a live button, are both worse than either alone.
 *
 * `true` verified · `false` not · `null` unknowable · `undefined` still loading.
 * **Only `false` means "block".** Treating the other three as unverified would disable
 * enrolment for everyone whenever the request hiccups.
 */
export function useAccountVerified(username: string | null | undefined) {
  const { data } = useQuery({
    queryKey: ['account-verified', username],
    queryFn: () => isAccountVerified(username ?? ''),
    enabled: Boolean(username),
    // Verification happens out-of-band — the learner clicks the link in another tab or on
    // their phone. Re-checking on focus is what makes the notice disappear when they come
    // back, instead of lingering and looking broken.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  return data;
}

export default function VerifyAccountNotice({
  username,
  variant = 'banner',
  message,
}: Props) {
  const [result, setResult] = useState<ResendResult | null>(null);
  const [sending, setSending] = useState(false);
  const verified = useAccountVerified(username);

  // `undefined` while loading, `null` when unknowable. Both mean: say nothing yet.
  if (verified === undefined || verified === null || verified === true) return null;

  async function onResend() {
    setSending(true);
    try {
      setResult(await resendActivation());
    } finally {
      setSending(false);
    }
  }

  const body =
    message ??
    'Verify your email address to finish setting up your account. We sent you a link when you signed up.';

  return (
    <div
      className={`verify-notice verify-notice--${variant}`}
      role="status"
      data-testid="verify-account-notice"
    >
      <Icon name="mark_email_unread" className="verify-notice__icon" />
      <div className="verify-notice__body">
        <p className="verify-notice__text">{body}</p>
        {result && <p className="verify-notice__result">{RESEND_COPY[result]}</p>}
      </div>
      <button
        type="button"
        className="verify-notice__action"
        onClick={onResend}
        // Once it has been sent, sending again mostly burns the learner's hourly quota.
        disabled={sending || result === 'sent' || result === 'already_active'}
      >
        {sending ? 'Sending…' : 'Resend email'}
      </button>
    </div>
  );
}
